import type { CartesianPose } from '@sb/geometry';
import { additionBetweenPoses, invertPose } from '@sb/geometry';

export interface MoveToStartInput {
  pin: 'start' | 'end' | 'reference';
  // The target's raw pose (the position-list entry the operator selected).
  // Used as-is by pin === 'reference'; ignored by start/end.
  targetPose: CartesianPose;
  // The target reduced to the wrist frame by its taught tcpOption. The caller
  // does the reduction so this helper stays pure (no RoutineContext). Used by
  // start/end.
  targetWrist: CartesianPose;
  // Window endpoints in wrist frame; equal to frames[0]/frames[-1] when the
  // trim is disabled.
  startFrame: CartesianPose;
  endFrame: CartesianPose;
  // Reference's world pose at record time. Required for pin === 'reference';
  // ignored otherwise.
  refWorldPose: CartesianPose | undefined;
  // Frame re-expression (record TCP · inv(live TCP)).
  frameTransform: CartesianPose;
}

/**
 * Wrist move-to-start pose for a demonstration replay, per pin mode.
 *
 *   - start     → targetWrist (window start lands at the taught target)
 *   - end       → composed so window end lands at the taught target
 *   - reference → shift recording by targetPose · inv(refWorldPose),
 *                 anchored at the frame-transformed window start
 *
 * Pure: no I/O, no RoutineContext.
 */
export function computeMoveToStart(input: MoveToStartInput): CartesianPose {
  const {
    pin,
    targetPose,
    targetWrist,
    startFrame,
    endFrame,
    refWorldPose,
    frameTransform,
  } = input;

  if (pin === 'reference') {
    if (!refWorldPose) {
      throw new Error(
        "RunDemonstration: pin === 'reference' requires the recording to have a reference frame",
      );
    }

    // Full-pose re-grounding: the path rotates by whatever orientation delta
    // the two reference detections carry. Whether that delta is meaningful
    // is the locate skill's call — with `matches.enableRotation` off,
    // detections come back rotation-zeroed in the plane
    // (applyDisableRotation), so the shift degenerates to pure translation.
    const referenceShift = additionBetweenPoses(
      targetPose,
      invertPose(refWorldPose),
    );

    const transformedStart = additionBetweenPoses(startFrame, frameTransform);

    return additionBetweenPoses(referenceShift, transformedStart);
  }

  if (pin === 'start') {
    return targetWrist;
  }

  const transformedStart = additionBetweenPoses(startFrame, frameTransform);
  const transformedEnd = additionBetweenPoses(endFrame, frameTransform);

  return additionBetweenPoses(
    additionBetweenPoses(targetWrist, invertPose(transformedEnd)),
    transformedStart,
  );
}
